import { useState, useRef, useCallback } from 'react';
import {
  useVerifyExternalPayment,
  useSendExternalPaymentData,
  useAnalyzeXnetFile,
  useGenerateXnetFile,
} from '../../../hooks/useCoupons';
import { useRecLegalEntities } from '../../../hooks/useLegalEntities';
import { useSalePoints } from '../../../hooks/useSalePoints';
import { useUserLogged } from '../../../hooks/useUserLogged';

export const useExternalPayment = () => {
  const { branch, uuid } = useUserLogged();
  const { data: recLegalEntities = [] } = useRecLegalEntities();
  const { data: salePoints = [] } = useSalePoints();

  // Estados principales
  const [selectedFile, setSelectedFile] = useState(null);
  const [apiResponse, setApiResponse] = useState(null);
  const [verificationData, setVerificationData] = useState(null);
  const [lastLineData, setLastLineData] = useState(null);
  const [processResults, setProcessResults] = useState(null);
  const [paymentProvider, setPaymentProvider] = useState('XNET');

  // Estados para análisis XNET
  const [selectedAnalysisFile, setSelectedAnalysisFile] = useState(null);
  const [analysisResults, setAnalysisResults] = useState(null);
  const [showAnalysisDialog, setShowAnalysisDialog] = useState(false);

  // Estados de UI
  const [openErrorDialog, setOpenErrorDialog] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [showResultsDialog, setShowResultsDialog] = useState(false);
  const [loading, setLoading] = useState(false);

  // Refs
  const fileInputRef = useRef(null);
  const analysisFileInputRef = useRef(null);

  // Mutations
  const verifyMutation = useVerifyExternalPayment();
  const sendMutation = useSendExternalPaymentData();
  const analyzeMutation = useAnalyzeXnetFile();
  const generateXnetMutation = useGenerateXnetFile();

  const getApiErrorPayload = useCallback((error, fallbackMessage) => {
    return (
      error?.response?.data ||
      error?.message ||
      fallbackMessage
    );
  }, []);

  // Handlers
  const handleFileUpload = useCallback((event) => {
    setSelectedFile(event.target.files[0]);
  }, []);

  const handleVerify = useCallback(async () => {
    if (!selectedFile) {
      setOpenErrorDialog(true);
      return;
    }

    try {
      setLoading(true);
      const response = await verifyMutation.mutateAsync({
        file: selectedFile,
        provider: paymentProvider,
      });

      setVerificationData(response.coupons);
      setLastLineData(response.last_line_data);
      setIsDialogOpen(true);
    } catch (error) {
      setApiResponse(
        getApiErrorPayload(
          error,
          'Hubo un error al verificar el archivo. Por favor, intenta nuevamente.'
        )
      );
    } finally {
      setLoading(false);
    }
  }, [selectedFile, paymentProvider, verifyMutation, getApiErrorPayload]);

  const handleConfirmPayment = useCallback(
    async (entidadRecaudadora, puntoDeVenta, electronicInvoice, fechaFactura) => {
      if (!branch?.[0]?.uuid_branch || !uuid) {
        setApiResponse('Error: Datos de usuario incompletos');
        return;
      }

      setLoading(true);

      try {
        const result = await sendMutation.mutateAsync({
          uuid_user: uuid,
          uuid_branch: branch[0].uuid_branch,
          entidadRecaudadora,
          puntoDeVenta,
          electronicInvoice,
          fechaFactura,
          file: selectedFile,
          provider: paymentProvider,
        });

        setProcessResults(result);
        setShowResultsDialog(true);

        setIsDialogOpen(false);
        setVerificationData(null);
        setSelectedFile(null);

        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } catch (error) {
        setApiResponse(
          getApiErrorPayload(
            error,
            'Hubo un error al procesar el pago. Por favor, intenta nuevamente.'
          )
        );
      } finally {
        setLoading(false);
      }
    },
    [branch, uuid, selectedFile, paymentProvider, sendMutation, getApiErrorPayload]
  );

  // Handlers para análisis XNET
  const handleAnalysisFileUpload = useCallback((event) => {
    setSelectedAnalysisFile(event.target.files[0]);
  }, []);

  const handleAnalyzeXnet = useCallback(async () => {
    if (!selectedAnalysisFile) {
      setOpenErrorDialog(true);
      return;
    }

    try {
      setLoading(true);
      const response = await analyzeMutation.mutateAsync({
        file: selectedAnalysisFile,
      });

      setAnalysisResults(response);
      setShowAnalysisDialog(true);
    } catch (error) {
      setApiResponse(
        getApiErrorPayload(
          error,
          'Hubo un error al analizar el archivo. Por favor, intenta nuevamente.'
        )
      );
    } finally {
      setLoading(false);
    }
  }, [selectedAnalysisFile, analyzeMutation, getApiErrorPayload]);

  const handleGenerateXnetFile = useCallback(
    async (selectedCoupons) => {
      try {
        setLoading(true);
        await generateXnetMutation.mutateAsync({
          selectedCoupons,
        });
      } catch (error) {
        setApiResponse(
          getApiErrorPayload(
            error,
            'Hubo un error al generar el archivo. Por favor, intenta nuevamente.'
          )
        );
      } finally {
        setLoading(false);
      }
    },
    [generateXnetMutation, getApiErrorPayload]
  );

  // Close handlers
  const handleCloseError = useCallback(() => {
    setOpenErrorDialog(false);
  }, []);

  const handleCloseApiResponse = useCallback(() => {
    setApiResponse(null);
  }, []);

  const handleCloseVerification = useCallback(() => {
    setIsDialogOpen(false);
  }, []);

  const handleCloseResults = useCallback(() => {
    setShowResultsDialog(false);
  }, []);

  const handleCloseAnalysis = useCallback(() => {
    setShowAnalysisDialog(false);
  }, []);

  return {
    // Estados
    selectedFile,
    apiResponse,
    verificationData,
    lastLineData,
    processResults,
    paymentProvider,

    // Estados para análisis XNET
    selectedAnalysisFile,
    analysisResults,
    showAnalysisDialog,

    // Estados de UI
    openErrorDialog,
    isDialogOpen,
    showResultsDialog,
    loading:
      loading ||
      verifyMutation.isPending ||
      sendMutation.isPending ||
      analyzeMutation.isPending ||
      generateXnetMutation.isPending,

    // Datos externos
    recLegalEntities,
    salePoints,

    // Handlers
    handleFileUpload,
    handleVerify,
    handleConfirmPayment,
    handleCloseError,
    handleCloseApiResponse,
    handleCloseVerification,
    handleCloseResults,
    setPaymentProvider,

    // Handlers para análisis XNET
    handleAnalysisFileUpload,
    handleAnalyzeXnet,
    handleCloseAnalysis,
    handleGenerateXnetFile,

    // Refs
    fileInputRef,
    analysisFileInputRef,
  };
};
